import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState, StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Flag, ArrowRight, Bell } from "lucide-react";
import { toast } from "sonner";

function isOverdue(f) {
  if (["closed", "verified"].includes(f.status)) return false;
  if (!f.due_date) return false;
  return f.due_date < new Date().toISOString().slice(0, 10);
}

export default function Followups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/followups").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <Loader label="Loading follow-ups" />;

  const canRemind = ["am", "pd", "admin"].includes(user.role);
  const sendReminders = async () => {
    try { const { data } = await api.post("/followups/send-reminders"); toast.success(data.message); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        overline="Governance"
        title="Follow-ups"
        description={user.role === "tac" ? "Action items assigned to you. Respond with the action taken and evidence." : "Track, verify and close governance follow-ups across partners."}
        actions={canRemind && <Button variant="outline" onClick={sendReminders} className="rounded-md" data-testid="send-reminders-button"><Bell className="h-4 w-4 mr-1.5" aria-hidden="true" /> Send reminders</Button>}
      />
      {items.length === 0 ? (
        <EmptyState icon={Flag} title="No follow-ups" description="Governance follow-ups will appear here." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-wider">Title</TableHead>
                {user.role !== "tac" && <TableHead className="text-xs uppercase tracking-wider">Partner</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider">Priority</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Due</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((f, i) => (
                <motion.tr key={f.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="border-b border-border/60 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => navigate(`/followups/${f.id}`)} data-testid={`followup-row-${f.id}`}>
                  <TableCell className="py-3 font-medium text-sm">{f.title}</TableCell>
                  {user.role !== "tac" && <TableCell className="py-3 text-sm">{f.tac_name}</TableCell>}
                  <TableCell className="py-3 text-sm capitalize">{f.priority}</TableCell>
                  <TableCell className="py-3 text-sm font-mono">
                    {f.due_date}
                    {isOverdue(f) && <span className="ml-2 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" data-testid={`overdue-${f.id}`}>Overdue</span>}
                    {f.escalation_level >= 3 && <span className="ml-1 inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">Escalated</span>}
                  </TableCell>
                  <TableCell className="py-3"><StatusBadge status={f.status} /></TableCell>
                  <TableCell className="py-3 text-right pr-4"><ArrowRight className="h-4 w-4 text-muted-foreground inline" aria-hidden="true" /></TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
